import { defaultScroll, instaScroll } from '../scroll-to-position';

type OverscrollProps = {
  element: HTMLDivElement;
  isBannerCarousel: boolean | undefined;
  currentChild: number;
  setCurrentChild: (newChild: number) => void;
  gap: number;
  onChange: ((scroll: number) => void) | undefined;
  setInScrollNow: (isScroll: boolean) => void;
  direction: 'left' | 'right' | undefined;
  fullScroll: boolean | undefined;
  stepQuantity?: number;
};

type BannerOverscrollProps = Pick<
  OverscrollProps,
  'element' | 'currentChild' | 'setCurrentChild' | 'onChange' | 'setInScrollNow' | 'direction'
> & {
  step: number;
};

type BannerOffsetProps = Pick<BannerOverscrollProps, 'currentChild' | 'direction' | 'step'> & {
  scrollLeft: number;
};
type OffsetProps = {
  scrollLeft: number;
  step: number;
  currentChild: number;
  direction: OverscrollProps['direction'];
  fullScroll: OverscrollProps['fullScroll'];
  stepQuantity: number;
  childrenLength: number;
};
const getStep = ({ element, currentChild, gap, direction, fullScroll }: OverscrollProps) => {
  const { children } = element;
  let child = children[currentChild];
  if (direction === 'left') {
    if (currentChild === 0) {
      child = children[children.length - 1];
    } else if (fullScroll) {
      child = children[0];
    } else {
      child = children[currentChild - 1];
    }
  } else if (fullScroll) {
    child = children[children.length - 1];
  }
  return child.clientWidth + gap;
};
const getBannerOffset = ({ scrollLeft, step, currentChild, direction }: BannerOffsetProps) => {
  if (direction === 'left') return scrollLeft - step;
  if (direction === 'right') return scrollLeft + step;
  return step * currentChild;
};
const handleBannerOverscroll = ({
  element,
  currentChild,
  setCurrentChild,
  onChange,
  setInScrollNow,
  direction,
  step,
}: BannerOverscrollProps) => {
  const sectionLength = element.children.length / 3;
  const isAtStart = currentChild < sectionLength;
  const isAtEnd = currentChild >= 2 * sectionLength;
  if (direction === 'left' && isAtStart) {
    const offset = step * (sectionLength + currentChild);
    instaScroll(offset, element).then(() => {
      const nextOffset = step * (sectionLength + currentChild - 1);
      if (onChange) onChange(nextOffset);
      defaultScroll(nextOffset, element).then(() => {
        setInScrollNow(false);
        setCurrentChild(sectionLength + currentChild - 1);
      });
    });
    return true;
  }

  if (direction === 'right' && isAtEnd) {
    const offset = step * (currentChild - sectionLength);
    instaScroll(offset, element).then(() => {
      const nextOffset = step * (currentChild - sectionLength + 1);
      defaultScroll(nextOffset, element).then(() => {
        if (onChange) onChange(nextOffset);
        setInScrollNow(false);
        setCurrentChild(currentChild - sectionLength + 1);
      });
    });
    return true;
  }

  if (isAtStart) {
    const offset = step * (sectionLength + currentChild);
    instaScroll(offset, element).then(() => {
      const nextOffset = step * (sectionLength + currentChild + 1);
      defaultScroll(nextOffset, element).then(() => {
        if (onChange) onChange(nextOffset);
        setInScrollNow(false);
        setCurrentChild(sectionLength + currentChild + 1);
      });
    });
    return true;
  }

  if (isAtEnd) {
    const offset = step * (currentChild - sectionLength);
    instaScroll(offset, element).then(() => {
      setCurrentChild(currentChild - sectionLength);
      setInScrollNow(false);
      if (onChange) onChange(offset);
    });
    return true;
  }

  return false;
};

const getOffset = ({
  scrollLeft,
  step,
  currentChild,
  direction,
  fullScroll,
  stepQuantity,
  childrenLength,
}: OffsetProps) => {
  if (direction === 'left') {
    if (fullScroll) return 0;
    if (currentChild === 0) return step * (childrenLength - 1);
    return scrollLeft - step * stepQuantity;
  }

  if (direction === 'right') {
    if (fullScroll) return step * (childrenLength - 1);
    if (currentChild === childrenLength - 1) return 0;
    return scrollLeft + step * stepQuantity;
  }

  return step * currentChild;
};

export const overscroll = ({
  element,
  isBannerCarousel,
  currentChild,
  setCurrentChild,
  gap,
  onChange,
  setInScrollNow,
  direction,
  fullScroll,
  stepQuantity = 1,
}: OverscrollProps) => {
  const { scrollLeft } = element;
  const step = getStep({
    element,
    isBannerCarousel,
    currentChild,
    setCurrentChild,
    gap,
    onChange,
    setInScrollNow,
    direction,
    fullScroll,
    stepQuantity,
  });
  setInScrollNow(true);

  if (
    isBannerCarousel &&
    handleBannerOverscroll({
      element,
      currentChild,
      setCurrentChild,
      onChange,
      setInScrollNow,
      direction,
      step,
    })
  ) {
    return { offset: 0, stop: true };
  }

  const offset = isBannerCarousel
    ? getBannerOffset({ scrollLeft, step, currentChild, direction })
    : getOffset({
        scrollLeft,
        step,
        currentChild,
        direction,
        fullScroll,
        stepQuantity,
        childrenLength: element.children.length,
      });

  return { offset, stop: false };
};
